import { useEffect, useState } from "react";
import { Box, CircularProgress, Typography, Alert, Button } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import apiClient from "@/services/api/client";
import { API_ENDPOINTS } from "@/services/api/endpoints";
import { useAppDispatch } from "@/store/redux";
import { loginSuccess } from "@/store/redux/slices/authSlice";
import { ROUTES } from "@/routes/config";
import type { User } from "@/types";

/**
 * SSO OAuth callback page for platform admin.
 *
 * After Microsoft redirects back, the user-service issues a final redirect to:
 *   /auth/callback?code={one_time_code}
 *
 * This page exchanges the one-time code for JWT tokens via a secure API call,
 * stores tokens, and redirects to the dashboard.
 */
export default function AuthCallback() {
  const [error, setError] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get("code");

        // Clear the code from the URL immediately
        window.history.replaceState(null, "", window.location.pathname);

        if (!code) {
          setError("Authentication failed. No authorization code received.");
          return;
        }

        // Exchange one-time code for tokens
        const { data } = await apiClient.post(
          API_ENDPOINTS.SSO.SYSTEM_EXCHANGE,
          { code }
        );

        const { access_token, refresh_token, user } = data;

        // Store refresh token
        localStorage.setItem("refresh_token", refresh_token);

        // Build user object
        const mappedUser: User = {
          id: user.id,
          email: user.email,
          name: user.first_name
            ? `${user.first_name} ${user.last_name || ""}`.trim()
            : user.email.split("@")[0],
          mustChangePassword: user.must_change_password ?? false,
          hasPassword: user.has_password ?? true,
        };

        // Dispatch login success to Redux
        dispatch(loginSuccess({ user: mappedUser, token: access_token }));

        // Navigate
        if (mappedUser.mustChangePassword) {
          navigate(ROUTES.CHANGE_PASSWORD, { replace: true });
        } else {
          navigate(ROUTES.DASHBOARD_HOME, { replace: true });
        }
      } catch {
        setError(
          "Authentication failed. The code may have expired or already been used."
        );
      }
    };

    handleCallback();
  }, [dispatch, navigate, searchParams]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
      }}
    >
      {error ? (
        <>
          <Alert severity="error" sx={{ maxWidth: 400 }}>
            {error}
          </Alert>
          <Button variant="text" onClick={() => navigate(ROUTES.LOGIN)}>
            Back to login
          </Button>
        </>
      ) : (
        <>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Completing sign-in...
          </Typography>
        </>
      )}
    </Box>
  );
}
